import { gsap } from 'gsap';

class Accordion {
  el: HTMLElement;
  head: HTMLElement;
  content: HTMLElement;
  contentWrapper: HTMLElement;
  link: HTMLAnchorElement;
  icon: HTMLElement;
  index: number;
  isOpen: boolean;
  onOpen: () => void;

  constructor(el: HTMLElement, onOpen: () => void, index: number) {
    this.el = el;
    this.index = index;
    this.head = el.querySelector('.horizontal-accordion__head');
    this.content = el.querySelector('.horizontal-accordion__content');
    this.contentWrapper = el.querySelector(
      '.horizontal-accordion__content-wrapper'
    );
    this.link = this.content.querySelector('a');
    this.icon = el.querySelector('.horizontal-accordion__icon');
    this.onOpen = onOpen;
    this.isOpen = false;

    this.init();
  }

  private accessibilityOpen() {
    this.head.setAttribute('aria-expanded', 'true');
    this.head.setAttribute('tabindex', '-1');
    this.link.setAttribute('tabindex', '0');
  }

  private accessibilityClosed() {
    this.head.setAttribute('aria-expanded', 'false');
    this.head.setAttribute('tabindex', '0');
    this.link.setAttribute('tabindex', '-1');
  }

  open(e?: KeyboardEvent) {
    if (this.isOpen) return;
    if (e && e?.key !== 'Enter') return;

    this.el.classList.add('horizontal-accordion--open');
    this.contentWrapper.classList.add(
      'horizontal-accordion__content-wrapper--open'
    );
    this.icon.classList.add('icon-minus');
    this.icon.classList.remove('icon-plus');
    this.isOpen = true;
    this.accessibilityOpen();

    gsap.fromTo(
      this.content.children,
      {
        opacity: 0,
        x: 20,
      },
      {
        opacity: 1,
        x: 0,
        delay: 0.2,
        stagger: 0.3,
      }
    );
    this.onOpen();
  }

  close() {
    this.el.classList.remove('horizontal-accordion--open');
    this.contentWrapper.classList.remove(
      'horizontal-accordion__content-wrapper--open'
    );
    this.icon.classList.remove('icon-minus');
    this.icon.classList.add('icon-plus');
    this.isOpen = false;
    this.accessibilityClosed();

    gsap.to(this.content.children, {
      opacity: 0,
    });
  }

  setHeight() {
    this.el.style.setProperty(
      '--accordion-content-height',
      `${this.content.scrollHeight}px`
    );
  }

  init() {
    this.head.addEventListener('click', () => this.open());
    this.head.addEventListener('keydown', e => this.open(e));
    window.addEventListener('resize', () => this.setHeight());
    this.setHeight();
    this.accessibilityOpen();
  }
}

function horizontalAccordion() {
  const container = document.querySelector<HTMLElement>(
    '.horizontal-accordion-container'
  );

  if (!container) return;

  const els = container.querySelectorAll<HTMLElement>(
      '.js-horizontal-accordion'
    ),
    headEls = container.querySelectorAll<HTMLElement>(
      '.horizontal-accordion__head'
    ),
    accordions: Accordion[] = [];
  let activeAccordion: undefined | number;

  const onOpen = (index: number) => {
    activeAccordion = index;

    for (const accordion of accordions) {
      if (accordion.index !== activeAccordion) {
        accordion.close();
      }
    }
  };

  const setWidth = () => {
    const headWidths = Array.from(headEls)
      .map(el => el.offsetWidth)
      .reduce((width, current) => width + current);

    container.style.setProperty(
      '--accordion-content-width',
      `${container.offsetWidth - headWidths - headEls.length}px`
    );
  };

  els.forEach((el, i) => {
    accordions.push(new Accordion(el, () => onOpen(i), i));
  });

  window.addEventListener('resize', setWidth);
  setWidth();
  accordions[0].open();
}

horizontalAccordion();
